import React, { useMemo, useState } from "react";
import predictionMeta from "../data/modeling/livePredictionMeta.json";

const fieldDefinitions = [
  {
    key: "airt",
    label: "Air Temperature",
    unit: "deg F",
    step: "0.1",
    description: "Daily average air temperature used by the trained model.",
  },
  {
    key: "prec",
    label: "Precipitation",
    unit: "in",
    step: "0.01",
    description: "Daily precipitation total.",
  },
  {
    key: "slrt",
    label: "Solar Radiation",
    unit: "MJ/m^2",
    step: "0.1",
    description: "Daily average solar radiation from the historical station dataset.",
  },
  {
    key: "wspd",
    label: "Wind Speed",
    unit: "mph",
    step: "0.1",
    description: "Daily average wind speed.",
  },
];

const seasonExampleDates = {
  warm: "2024-07-15",
  cold: "2024-01-15",
};

function getTodayDateString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getSeasonFromDate(dateString) {
  const month = Number(dateString.slice(5, 7));
  return month >= 4 && month <= 9 ? "warm" : "cold";
}

function getSeasonLabel(season) {
  return season === "warm" ? "Warm" : "Cold";
}

function buildExampleState(season, date = seasonExampleDates[season]) {
  const example = predictionMeta.exampleInputs[season];
  return {
    date,
    airt: String(example.airt),
    prec: String(example.prec),
    slrt: String(example.slrt),
    wspd: String(example.wspd),
  };
}

const PredictionsPage = () => {
  const today = useMemo(() => getTodayDateString(), []);
  const [formValues, setFormValues] = useState(() =>
    buildExampleState(getSeasonFromDate(today), today)
  );
  const [result, setResult] = useState(null);
  const [requestError, setRequestError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inferredSeason = getSeasonFromDate(formValues.date);
  const currentFeatureSummary = predictionMeta.featureSummary[inferredSeason];
  const seasonLabel = getSeasonLabel(inferredSeason);

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setRequestError("");

    try {
      const response = await fetch("/api/predict", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formValues),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Prediction request failed.");
      }

      setResult(data);
    } catch (error) {
      setResult(null);
      setRequestError(
        error.message.includes("Failed to fetch")
          ? "Prediction API is unavailable in the local CRA dev server. This route works on Cloudflare Pages Functions or with local Pages emulation."
          : error.message
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function updateField(key, value) {
    setFormValues((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function loadSeasonExample(season) {
    setResult(null);
    setRequestError("");
    setFormValues(buildExampleState(season));
  }

  return (
    <div className="page-content results-page">
      <h1>Soil Temperature Prediction</h1>

      <div className="status-banner">
        Season-aware random forest trained on {predictionMeta.training.rowsTrain} daily
        records. Estimates soil temperature at 10 cm depth from weather conditions.
      </div>

      <div className="metric-strip">
        <div className="metric-tile">
          <span>Overall RMSE</span>
          <b>{predictionMeta.metrics.overall.rmse} deg F</b>
        </div>
        <div className="metric-tile">
          <span>Holdout Rows</span>
          <b>{predictionMeta.training.rowsTest}</b>
        </div>
        <div className="metric-tile">
          <span>Active Season</span>
          <b>{seasonLabel}</b>
        </div>
      </div>

      <div className="weather-container">
        <div className="weather-group">
          <div className="weather-header">
            <h3>Prediction Goal</h3>
          </div>
          <div className="results-copy">
            <p>
              Estimate soil temperature at 10 cm depth from daily air temperature, precipitation,
              solar radiation, and wind speed.
            </p>
            <p>
              The model switches by date: warm-season predictions use April through September, and
              cold-season predictions use October through March.
            </p>
            <p>
              Inputs are checked against the training range before the result is shown.
            </p>
          </div>
        </div>

        <div className="weather-group">
          <div className="weather-header">
            <h3>Model Context</h3>
          </div>
          <div className="results-copy">
            <p>
              Training window ended on <b>{predictionMeta.training.trainEnd}</b>.
            </p>
            <p>
              Holdout test window: <b>{predictionMeta.training.testStart}</b> to{" "}
              <b>{predictionMeta.training.testEnd}</b>.
            </p>
            <p>
              Current season mode: <b>{seasonLabel}</b>.
            </p>
          </div>
        </div>
      </div>

      <div className="weather-container">
        <div className="weather-group results-card-full">
          <div className="weather-header">
            <h2>Prediction Inputs</h2>
          </div>
          <div className="weather-body">
            <form className="prediction-form" onSubmit={handleSubmit}>
              <label className="prediction-field">
                <span>Date</span>
                <input
                  type="date"
                  value={formValues.date}
                  onChange={(event) => updateField("date", event.target.value)}
                  required
                />
              </label>

              {fieldDefinitions.map((field) => {
                const range = currentFeatureSummary[field.key];

                return (
                  <label className="prediction-field" key={field.key}>
                    <span>
                      {field.label} ({field.unit})
                    </span>
                    <input
                      type="number"
                      step={field.step}
                      value={formValues[field.key]}
                      onChange={(event) => updateField(field.key, event.target.value)}
                      required
                    />
                    <small>
                      {field.description} Typical {inferredSeason} range: {range.min} to {range.max}.
                    </small>
                  </label>
                );
              })}

              <div className="prediction-actions">
                <button type="button" onClick={() => loadSeasonExample("warm")}>
                  Load Warm Example
                </button>
                <button type="button" onClick={() => loadSeasonExample("cold")}>
                  Load Cold Example
                </button>
                <button className="prediction-submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Predicting..." : "Tap to Predict Soil Temperature"}
                </button>
              </div>
            </form>

            {requestError ? <div className="prediction-error">{requestError}</div> : null}
          </div>
        </div>
      </div>

      {result ? (
        <div className="weather-container">
          <div className="weather-group">
            <div className="weather-header">
              <h3>Prediction Result</h3>
            </div>
            <div className="results-copy">
              <div className="prediction-value">
                <span>{result.prediction.soilTemperatureF}</span>
                <b>deg F</b>
              </div>
              <p>
                Estimated soil temperature at 10 cm depth.
              </p>
              <p>
                Metric conversion: <b>{result.prediction.soilTemperatureC} deg C</b>
              </p>
              <p>
                Seasonal model used:{" "}
                <b>{result.input.season === "warm" ? "Warm-season random forest" : "Cold-season random forest"}</b>
              </p>
            </div>
          </div>

          <div className="weather-group">
            <div className="weather-header">
              <h3>Reliability Notes</h3>
            </div>
            <div className="results-copy">
              <p>
                Season holdout RMSE: <b>{result.model.estimatedHoldoutRmse} deg F</b>
              </p>
              <p>
                Season holdout MAE: <b>{result.model.estimatedHoldoutMae} deg F</b>
              </p>
              <p>
                Comparable evaluation rows for this season: <b>{result.model.seasonTestRows}</b>
              </p>
            </div>
          </div>

          <div className="weather-group">
            <div className="weather-header">
              <h3>Input Warnings</h3>
            </div>
            <div className="results-copy">
              {result.warnings.length > 0 ? (
                <ul className="prediction-warning-list">
                  {result.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              ) : (
                <p>All inputs are within the historical training range for this season.</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PredictionsPage;
